import { Component, ComponentRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { delay, filter, finalize, switchMap, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { BehaviorSubject, EMPTY, NEVER, Observable, of, ReplaySubject } from 'rxjs';
import { EntrySummaryCountByStatusModel, EntrySummaryModel, EntrySummaryShortModel } from '@data-layer/models/entry-summaries';
import { AppLayoutService } from '@app/modules/app-layout/app-layout.service';
import { CompaniesService } from '@services/companies.service';
import { BatchImportRepository, EntrySummaryRepository } from '@data-layer/repositories';
import { EntrySummaryService } from '@services/entry-summary.service';
import { BatchImportModel, PaginatedResultModel } from '@data-layer/models/common';
import { enumHeaderActionId } from '@app/modules/app-layout/enums';
import { EsFilterType } from '../../models';
import { SearchHelper } from '@helpers/search-helper';
import { IHeaderAction } from '@app/modules/app-layout/interfaces';
import { UntypedFormControl } from '@angular/forms';
import { enumEntrySummaryStatusTab } from '../../enums';
import { enumEntrySummaryState, enumEntrySummarySubmissionType } from '@data-layer/enums/entry-summaries';
import { InternalNotesComponent } from '@shared/internal-notes/components';
import { InternalNote } from '@shared/internal-notes/interfaces';
import { AppService } from '@app/app.service';
import { EsChatDesktopComponent } from '..';
import { EntrySummaryStateService } from '@shared/es-mutual/services';
import { groupBy, union } from 'lodash-es';
import { UtilsHelper } from '@helpers/utils-helper';
import { UserBetaFlagModel } from '@data-layer/models/user';
import { enumUserBetaFlag } from '@data-layer/enums/user';
import { GenericService } from '@services/generic.service';
import { EsBatchSidebarComponent } from '@shared/es-mutual/components';
import { enumPaymentState } from '@data-layer/enums/payments';
import { enumBatchImportStatus } from '@data-layer/enums/common';
import { Location } from '@angular/common';

@Component({
  selector: 'es-list',
  templateUrl: './es-list.component.html',
  styleUrls: ['./es-list.component.scss'],
})
export class EsListComponent implements OnInit, OnDestroy {
  @Input() public showButtonShowMore: boolean = false;
  @Input() public limit: number = 20;
  @Input() public loadNewOnScroll: boolean = true;
  @Input() public scrollContentClass: string;
  @Input() public isShowFilters: boolean = false;
  @Input() public isShowHeader: boolean = true;
  @Input() public importedFileName: string;
  @Input() public batchItem: BatchImportModel;
  @Output() public showMore: EventEmitter<any> = new EventEmitter<any>();
  @Output() public upload: EventEmitter<File> = new EventEmitter<File>();
  @Output() public uploadError: EventEmitter<void> = new EventEmitter<void>();
  @Output() public closeImport: EventEmitter<void> = new EventEmitter<void>();

  public esList: EntrySummaryShortModel[] = [];
  public paginatedList: PaginatedResultModel<EntrySummaryShortModel> = { total: 0, items: [], options: null };
  public groupedList: { [key: number]: EntrySummaryShortModel[] };
  public isShowList: boolean = false;
  public entrySummaryCountByStatus: EntrySummaryCountByStatusModel[];
  public enumEntrySummaryStatusTab: typeof enumEntrySummaryStatusTab = enumEntrySummaryStatusTab;
  public currentSelectedTab: enumEntrySummaryStatusTab = enumEntrySummaryStatusTab.ALL;
  public currentSelectedStatuses: enumEntrySummaryState[] = [];
  public searchParameterCtrl: UntypedFormControl = new UntypedFormControl();
  public isShowMobileImport: boolean;

  private filters: EsFilterType;
  private companyId: number;
  private isLoadingNew: boolean = false;
  private draftStates: enumEntrySummaryState[] = [
    enumEntrySummaryState.NOT_SENT,
    enumEntrySummaryState.DUTY_CALCULATION_PENDING,
    enumEntrySummaryState.DUTY_CALCULATION_COMPLETE,
  ];
  private processingStates: enumEntrySummaryState[] = [
    enumEntrySummaryState.BROKER_PROCESSING_PENDING,
    enumEntrySummaryState.SENT_TO_PROVIDER,
    enumEntrySummaryState.VALIDATED_AT_PROVIDER,
    enumEntrySummaryState.PROVIDER_SEND_PENDING,
    enumEntrySummaryState.DUTY_CALCULATION_PENDING,
    enumEntrySummaryState.NOT_VALID_PROVIDER,
    enumEntrySummaryState.BROKER_PROCESSING_FAILED,
    enumEntrySummaryState.NO_BILL_ON_FILE,
    enumEntrySummaryState.BILL_NOT_MATCHED,
    enumEntrySummaryState.REJECTED,
    enumEntrySummaryState.ACCEPTED,
    enumEntrySummaryState.UNRECOVERABLE,
    enumEntrySummaryState.BROKER_PROCESSING_FINISHED,
    enumEntrySummaryState.BROKER_PROCESSING_CARGO_RELEASE_PENDING,
    enumEntrySummaryState.AWAITING_ARRIVAL,
  ];
  private releasedStates: enumEntrySummaryState[] = [enumEntrySummaryState.APPROVED];
  private isESSent: boolean;
  private sendESs$: BehaviorSubject<void> = new BehaviorSubject<void>(null);
  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private entrySummaryService: EntrySummaryService,
    private appLayoutService: AppLayoutService,
    private companiesService: CompaniesService,
    private entrySummaryRepository: EntrySummaryRepository,
    private appService: AppService,
    private entrySummaryStateService: EntrySummaryStateService,
    private genericService: GenericService,
    private location: Location,
    private batchImportRepository: BatchImportRepository,
  ) {}

  public ngOnInit(): void {
    this.companyId = this.companiesService.getCompany() ? this.companiesService.getCompany().id : null;

    this.sendESs$
      .pipe(
        filter(() => !this.isESSent),
        switchMap(() => {
          if (!!this.batchItem) {
            return this.sendESList();
          } else {
            return EMPTY;
          }
        }),
        delay(300),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.isESSent = true;
        this.location.go('/entry-summary');
        this.esList = [];
        this.paginatedList = { total: 0, items: [], options: null };
        this.getEsCountStats();
        this.getNextEntrySummaries(true);
      });

    this.companiesService.companyChange$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.companyId = this.companiesService.getCompany()?.id;
      this.esList = [];
      this.paginatedList = { total: 0, items: [], options: null };
      this.getEsCountStats();
      this.getNextEntrySummaries(true);
    });
    this.appLayoutService.headerActions$
      .pipe(withLatestFrom(this.genericService.isLaptop$), take(1))
      .subscribe(([data, isLaptop]: [IHeaderAction[], boolean]) => {
        data.push({ id: enumHeaderActionId.ES, routerLink: ['entry-summary', 'start'], title: 'BUTTON.ADD_NEW_ES' });
        if (this.hasImportFeature && !isLaptop) {
          data.push({
            id: enumHeaderActionId.ES_BULK,
            routerLink: [],
            action: this.openEntrySummaryBulkSidebar.bind(this),
            title: 'BATCH_UPLOAD_ES',
          });
        }
        this.appLayoutService.headerActions$.next(UtilsHelper.uniqBy<IHeaderAction>(data, 'title'));
      });

    this.appLayoutService.openImportInput$.pipe(takeUntil(this.destroy$)).subscribe((val: boolean) => {
      this.isShowMobileImport = val;
    });
  }

  public ngOnDestroy(): void {
    this.appLayoutService.headerActions$.pipe(take(1)).subscribe((data: IHeaderAction[]) => {
      this.appLayoutService.headerActions$.next(
        data.filter((i: IHeaderAction) => ![enumHeaderActionId.ES, enumHeaderActionId.ES_BULK].includes(i.id)),
      );
    });
    this.destroy$.next();
  }

  public get draftsCount(): number {
    return this.calcItemCount(this.draftStates);
  }

  public get processingCount(): number {
    return this.calcItemCount(this.processingStates);
  }

  public get releasedCount(): number {
    return this.calcItemCount(this.releasedStates);
  }

  public get hasBrokerChat(): boolean {
    return !!(this.esList || []).find(
      (es: EntrySummaryShortModel) =>
        es.submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED &&
        (!!es.sentToProviderDate || es.state === enumEntrySummaryState.BROKER_REVIEW_PENDING),
    );
  }

  public get hasImportFeature(): boolean {
    return this.companiesService
      .getUser()
      ?.betaFlags?.map((flag: UserBetaFlagModel) => flag.tag)
      .includes(enumUserBetaFlag.importCustomsFromExcelFeature);
  }

  public get payedBatchItems(): EntrySummaryShortModel[] {
    if (!this.batchItem) {
      return;
    }

    return (this.esList || []).filter(
      (item: EntrySummaryShortModel) =>
        item.batchId === this.batchItem.id && [enumPaymentState.SUCCESS, enumPaymentState.DELAYED].includes(item.paymentState),
    );
  }

  public get pendingBatchItems(): EntrySummaryShortModel[] {
    if (!this.batchItem) {
      return;
    }

    return (this.esList || []).filter(
      (item: EntrySummaryShortModel) => item.batchId === this.batchItem.id && [enumPaymentState.PENDING].includes(item.paymentState),
    );
  }

  public get isShowBatchNotificationSuccess(): boolean {
    if (!this.batchItem) {
      return;
    }

    return (
      [enumBatchImportStatus.FINISHED, enumBatchImportStatus.REVIEWED].includes(this.batchItem.status) &&
      !this.batchItem.fileReference &&
      !!this.payedBatchItems.length
    );
  }

  public get isShowBatchNotificationPartial(): boolean {
    if (!this.batchItem) {
      return;
    }

    return (
      [enumBatchImportStatus.FINISHED, enumBatchImportStatus.REVIEWED].includes(this.batchItem.status) &&
      !!this.batchItem.fileReference &&
      !!this.payedBatchItems.length
    );
  }

  public get isShowBatchNotificationPending(): boolean {
    if (!this.batchItem) {
      return;
    }

    return (
      [enumBatchImportStatus.FINISHED, enumBatchImportStatus.REVIEWED].includes(this.batchItem.status) && !!this.pendingBatchItems.length
    );
  }

  public get paymentCountStr(): string {
    return this.payedBatchItems?.length > 1 ? 'BATCH_AFTER_PAYMENT_STEP_SUCCESS_PLURAL' : 'BATCH_AFTER_PAYMENT_STEP_SUCCESS_SINGLE';
  }

  public get paymentPendingCountStr(): string {
    return this.pendingBatchItems?.length > 1 ? 'BATCH_AFTER_PAYMENT_STEP_PENDING_PLURAL' : 'BATCH_AFTER_PAYMENT_STEP_PENDING_SINGLE';
  }

  public onShowMore(): void {
    this.showMore.emit();
  }

  public refreshEntrySummaries(filters?: EsFilterType): void {
    if (!!filters) {
      this.filters = SearchHelper.convertFiltersToParams({ ...filters });
    }

    this.getAndGroupEsList(true);
  }

  public getNextEntrySummaries(isReload: boolean): void {
    this.getAndGroupEsList(isReload);
  }

  public getEsCountStats(): void {
    this.entrySummaryRepository
      .getEsCountStats(this.companyId)
      .pipe(take(1))
      .subscribe((res: EntrySummaryCountByStatusModel[]) => {
        this.entrySummaryCountByStatus = res;
      });
  }

  public selectTab(status: enumEntrySummaryStatusTab): void {
    this.resetData(status);

    switch (status) {
      case enumEntrySummaryStatusTab.ALL:
        this.currentSelectedStatuses = [];
        break;
      case enumEntrySummaryStatusTab.DRAFTS:
        this.currentSelectedStatuses = this.draftStates;
        break;
      case enumEntrySummaryStatusTab.PROCESSING:
        this.currentSelectedStatuses = this.processingStates;
        break;
      case enumEntrySummaryStatusTab.RELEASED:
        this.currentSelectedStatuses = this.releasedStates;
        break;
    }

    this.refreshEntrySummaries({ states: this.currentSelectedStatuses, searchParameter: this.searchParameterCtrl.value });
  }

  public searchUpdated(term: string): void {
    this.resetData(enumEntrySummaryStatusTab.ALL);
    this.refreshEntrySummaries({ states: this.currentSelectedStatuses, searchParameter: term });
  }

  public calcItemCount(statuses: enumEntrySummaryState[]): number {
    return (this.entrySummaryCountByStatus || []).reduce(
      (acc: number, curr: EntrySummaryCountByStatusModel) => (statuses.includes(curr.state) ? acc + curr.entrySummaryCount : acc),
      0,
    );
  }

  public onEditInternalNotes(es: EntrySummaryModel): void {
    const componentSidebarRef: ComponentRef<InternalNotesComponent> = this.appService.openSidebar(InternalNotesComponent, {
      sidebarHeaderText: !!es.internalNotes ? 'LABELS.INTERNAL_NOTES_EDIT' : 'LABELS.INTERNAL_NOTES_ADD',
      tooltipText: !!es.internalNotes ? 'LABELS.INTERNAL_NOTES_EDIT_DESCRIPTION' : 'LABELS.INTERNAL_NOTES_ADD_DESCRIPTION',
    });
    componentSidebarRef.instance.internalNote = { internalNotes: es.internalNotes, internalReferenceNumber: es.internalReferenceNumber };
    componentSidebarRef.instance.isShowDetails = true;
    componentSidebarRef.instance.item = es;
    componentSidebarRef.instance.saveInternalNotes.pipe(takeUntil(this.destroy$)).subscribe((data: InternalNote) => {
      this.entrySummaryRepository
        .updateInternalNote(es.id, data)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.appLayoutService.internalNotesUpdated$.next();
          this.appService.closeSidebar();

          const index: number = this.paginatedList.items.findIndex((item: EntrySummaryShortModel) => item.id === es.id);

          this.paginatedList.items[index].internalNotes = data.internalNotes;
          this.paginatedList.items[index].internalReferenceNumber = data.internalReferenceNumber;
        });
    });
  }

  public onOpenChat(esRefNumber: string): void {
    this.entrySummaryStateService
      .loadEntrySummary(esRefNumber)
      .pipe(take(1))
      .subscribe((es: EntrySummaryModel) => {
        this.entrySummaryStateService.entrySummary$.next(es);

        const componentSidebarRef: ComponentRef<EsChatDesktopComponent> = this.appService.openSidebar(EsChatDesktopComponent, {
          sidebarHeaderText: 'BUTTON.MESSAGE_BROKER',
        });

        componentSidebarRef.instance.isShowInfo = true;
        this.entrySummaryStateService.entrySummary$
          .pipe(
            filter(
              (updatedEs: EntrySummaryModel) => !!updatedEs && updatedEs.referenceNumber === esRefNumber && !updatedEs.hasNewBrokerMessages,
            ),
            take(1),
            takeUntil(this.destroy$),
          )
          .subscribe(() => {
            const index: number = this.paginatedList.items.findIndex(
              (item: EntrySummaryShortModel) => item.referenceNumber === esRefNumber,
            );
            if (index !== -1) {
              this.paginatedList.items[index].hasNewBrokerMessages = false;
            }
          });
      });
  }

  public resetSearch(): void {
    this.resetData(enumEntrySummaryStatusTab.ALL);
    this.searchParameterCtrl.setValue('');
    this.refreshEntrySummaries({ states: this.currentSelectedStatuses });
  }

  private resetData(status: enumEntrySummaryStatusTab): void {
    this.currentSelectedTab = status;
    this.currentSelectedStatuses = [];
    this.paginatedList = { options: null, items: [], total: 0 };
    this.isShowList = false;
  }

  private getAndGroupEsList(reload: boolean): void {
    const offset: number = reload ? 0 : this.paginatedList.items.length;
    let limit: number = reload ? this.paginatedList.items.length : this.limit;
    if (limit < this.limit) {
      limit = this.limit;
    }
    this.getESList(offset, limit, reload)
      .pipe(
        tap((result: PaginatedResultModel<EntrySummaryShortModel>) => {
          this.paginatedList = reload
            ? result
            : {
                total: result.total,
                items: [...this.paginatedList.items, ...result.items],
                options: result.options,
              };
          this.groupByStatement();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.isShowList = true;
        this.sendESs$.next();
      });
  }

  private getESList(offset: number, limit: number, reload: boolean = false): Observable<PaginatedResultModel<EntrySummaryShortModel>> {
    if (
      !this.companyId ||
      (this.paginatedList.items.length > 0 && this.paginatedList.items.length === this.paginatedList.total && !reload) ||
      this.isLoadingNew
    ) {
      if (!this.companyId) {
        return of({ total: 0, items: [], options: null });
      }
      this.isShowList = true;
      return NEVER;
    }
    this.isLoadingNew = true;
    return this.entrySummaryRepository
      .getList(this.companyId, offset, limit, this.filters)
      .pipe(finalize(() => (this.isLoadingNew = false)));
  }

  private groupByStatement(): void {
    this.groupedList = groupBy(this.paginatedList.items, (es: EntrySummaryShortModel) => {
      return es.statement && es.statement.id;
    });
    let newList: EntrySummaryShortModel[] = [];
    for (const key in this.groupedList) {
      if (key) {
        const unitedESList: EntrySummaryShortModel[] = this.groupedList[key].map((es: EntrySummaryShortModel, index: number) => {
          const isUnitedStatement: boolean = !isNaN(+key);
          es.isUnitedStatement = isUnitedStatement;
          es.isFirstUnited = isUnitedStatement ? index === 0 : false;
          es.isLastUnited = isUnitedStatement ? index === this.groupedList[key].length - 1 : false;
          return es;
        });
        newList = union(newList, unitedESList);
      }
    }
    this.esList = newList;
  }

  private openEntrySummaryBulkSidebar(): void {
    const componentSidebarRef: ComponentRef<EsBatchSidebarComponent> = this.appService.openSidebar(EsBatchSidebarComponent, {
      sidebarHeaderText: 'ES_BATCH_UPLOAD',
    });
    componentSidebarRef.instance.onCreated.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.appService.closeSidebar();
    });
  }

  private sendESList(): Observable<never> {
    const items: string[] = (this.esList || [])
      ?.filter(
        (es: EntrySummaryShortModel) =>
          [enumPaymentState.SUCCESS, enumPaymentState.DELAYED].includes(es.paymentState) && es.batchId === this.batchItem.id,
      )
      .map((es: EntrySummaryShortModel) => es.referenceNumber);

    return items.length ? this.batchImportRepository.sendEntrySummaries(items) : NEVER;
  }
}
